/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { useRive, useStateMachineInput } from '@rive-app/react-canvas';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { PiStarFourFill } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/AppRoutes';
import coinIcon from '@/Assets/Images/coin-icon.svg?url';
import FlyingCoins from '@/components/FlyingCoins';
import useStore from '@/store';
import { getTriggerArrayForCoins } from '@/utils/helper';

const RIV_SRC = './rivs/CoinanimationPAL.riv';
const STATE_MACHINE_COIN2 = 'State Machine Coin2';

const FLY_DELAY_MS = 2300;
const FLY_DURATION_MS = 1200;
const CENTER_DIVISOR = 2;

export const COIN_TRIGGERS = ['1coin', '2coin', '3coin'] as const;
export type CoinTriggerName = (typeof COIN_TRIGGERS)[number];
export type CoinEarnedAmount = 1 | 2 | 3;
export type TriggerArrayElement = 1 | 2 | 3;

const TRIGGER_BY_DELTA: Record<TriggerArrayElement, CoinTriggerName> = {
  1: '1coin',
  2: '2coin',
  3: '3coin',
};

const TRIGGER_TO_COUNT: Record<CoinTriggerName, CoinEarnedAmount> = {
  '1coin': 1,
  '2coin': 2,
  '3coin': 3,
};

interface CoinsProperties {
  coins: number;
  onCoinsEarned?: (amount: CoinEarnedAmount) => void;
  enableRedirection?: boolean;
}

interface OverlayProperties {
  visible: boolean;
  triggerName: CoinTriggerName;
  stepKey: number;
  getTargetRect: () => DOMRect | undefined;
  onCoinsEarned: (amount: CoinEarnedAmount) => void;
  onClose: () => void;
}

function CoinsFullScreenOverlay({
  visible,
  triggerName,
  stepKey,
  getTargetRect,
  onCoinsEarned,
  onClose,
}: OverlayProperties): ReactElement | null {
  const [targetRect, setTargetRect] = useState<DOMRect | undefined>();
  const [flyStarted, setFlyStarted] = useState(false);
  const triggerFiredReference = useRef(false);

  const { rive, RiveComponent } = useRive({
    src: RIV_SRC,
    stateMachines: STATE_MACHINE_COIN2,
    autoplay: true,
  });
  const triggerInput = useStateMachineInput(rive, STATE_MACHINE_COIN2, triggerName);

  // const onBackdropKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
  //   if (event.key === 'Escape') onClose();
  // }, []);
  const onCanvasClick = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);
  const onCanvasKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  useEffect(() => {
    if (!visible) {
      triggerFiredReference.current = false;
      return () => {};
    }
    if (triggerInput && !triggerFiredReference.current) {
      triggerFiredReference.current = true;
      (triggerInput as { fire: () => void }).fire();
    }
    return () => {};
  }, [visible, triggerInput, stepKey]);

  useEffect(() => {
    if (visible) {
      triggerFiredReference.current = false;
      setFlyStarted(false);
      setTargetRect(undefined);
      const rect = getTargetRect();
      if (rect) setTargetRect(rect);
      else {
        const id = requestAnimationFrame(() => setTargetRect(getTargetRect()));
        return () => cancelAnimationFrame(id);
      }
    }
    return () => {};
  }, [visible, triggerName, stepKey, getTargetRect]);

  useEffect(() => {
    if (!visible || !targetRect) return () => {};
    const t = setTimeout(() => setFlyStarted(true), FLY_DELAY_MS);
    return () => clearTimeout(t);
  }, [visible, targetRect]);

  useEffect(() => {
    if (!visible || !targetRect || !flyStarted) return () => {};
    const amount = TRIGGER_TO_COUNT[triggerName];
    const t = setTimeout(() => {
      onCoinsEarned(amount);
      onClose();
    }, FLY_DURATION_MS);
    return () => clearTimeout(t);
  }, [visible, targetRect, flyStarted, triggerName, onCoinsEarned, onClose]);

  if (!visible) return null; // eslint-disable-line unicorn/no-null -- React render nothing

  const centerX = typeof window === 'undefined' ? 0 : window.innerWidth / CENTER_DIVISOR;
  const centerY = typeof window === 'undefined' ? 0 : window.innerHeight / CENTER_DIVISOR;

  const overlay = (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog backdrop with tabIndex
    <div
      role="dialog"
      aria-modal
      aria-label="Coins animation"
      tabIndex={-1}
      className="pointer-events-none fixed inset-0 z-coins-overlay flex items-center justify-center bg-black/40"
    >
      <div
        className="absolute flex h-[min(80vmin,24rem)] w-[min(80vmin,24rem)] flex-shrink-0 cursor-default items-center justify-center"
        onClick={onCanvasClick}
        onKeyDown={onCanvasKeyDown}
        role="presentation"
      >
        <RiveComponent />
      </div>
      {flyStarted && targetRect && (
        <FlyingCoins
          centerX={centerX}
          centerY={centerY}
          targetRect={targetRect}
          durationMs={FLY_DURATION_MS}
          coinIconSrc={coinIcon}
        />
      )}
    </div>
  );

  if (typeof document === 'undefined') {
    return null; // eslint-disable-line unicorn/no-null
  }
  return createPortal(overlay, document.body);
}

type OverlayState = { triggerName: CoinTriggerName; stepKey: number };

export default function Coins({
  coins,
  onCoinsEarned,
  enableRedirection,
}: CoinsProperties): ReactElement {
  const navigate = useNavigate();
  const [overlayState, setOverlayState] = useState<OverlayState | undefined>();
  const coinBadgeReference = useRef<HTMLButtonElement>(null);
  const triggerQueueReference = useRef<TriggerArrayElement[]>([]);
  const sequenceStepReference = useRef(0);

  const { pendingCoinAnimation, clearPendingCoinAnimation } = useStore();

  const onCoinsClick = useCallback(() => {
    if (!enableRedirection) return;
    navigate(ROUTES.STUDENT_PROFILE, { state: { isLeaderboard: true } });
  }, [navigate, enableRedirection]);

  const getTargetRect = useCallback(() => coinBadgeReference.current?.getBoundingClientRect(), []);

  const playTriggerSequence = useCallback((triggerArray: TriggerArrayElement[]) => {
    if (triggerArray.length === 0) return;
    triggerQueueReference.current = [...triggerArray];
    sequenceStepReference.current = 0;
    setOverlayState({
      triggerName: TRIGGER_BY_DELTA[triggerArray[0]],
      stepKey: 0,
    });
  }, []);

  const triggerCoinsAnimation = useCallback(
    (coinsCount: number) => {
      playTriggerSequence(getTriggerArrayForCoins(coinsCount));
    },
    [playTriggerSequence],
  );

  const onClose = useCallback(() => {
    const queue = triggerQueueReference.current.slice(1);
    triggerQueueReference.current = queue;
    if (queue.length > 0) {
      sequenceStepReference.current += 1;
      setOverlayState({
        triggerName: TRIGGER_BY_DELTA[queue[0]],
        stepKey: sequenceStepReference.current,
      });
    } else {
      setOverlayState(undefined);
    }
  }, []);

  useEffect(() => {
    if (pendingCoinAnimation > 0) {
      triggerCoinsAnimation(pendingCoinAnimation);
      clearPendingCoinAnimation();
    }
  }, [pendingCoinAnimation, triggerCoinsAnimation, clearPendingCoinAnimation]);

  const onCoinsEarnedReport = useCallback(
    (amount: CoinEarnedAmount) => onCoinsEarned?.(amount),
    [onCoinsEarned],
  );

  // const onCoinsClick = useCallback(() => triggerCoinsAnimation(3), [triggerCoinsAnimation]);

  return (
    <>
      <button
        ref={coinBadgeReference}
        type="button"
        onClick={onCoinsClick}
        className="relative flex h-[2.625rem] w-[6rem] cursor-pointer flex-row items-center justify-end rounded-[5.5em] bg-secondary-blue-7 py-0 pr-3"
        aria-label="Coins"
      >
        <img src={coinIcon} alt="" className="absolute -left-3 h-[3.7rem]" />
        <p className="font-outline-2-black w-[2rem] text-center font-lilita text-l2 text-neutral-white">
          {coins}
        </p>
        <PiStarFourFill className="absolute -top-1 right-[0.1rem] text-neutral-white" />
      </button>
      {overlayState !== undefined && (
        <CoinsFullScreenOverlay
          key={overlayState.stepKey}
          visible
          triggerName={overlayState.triggerName}
          stepKey={overlayState.stepKey}
          getTargetRect={getTargetRect}
          onCoinsEarned={onCoinsEarnedReport}
          onClose={onClose}
        />
      )}
    </>
  );
}

Coins.defaultProps = {
  onCoinsEarned: undefined,
  enableRedirection: false,
};
